<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import {
  IconClose,
  IconPlay,
  IconPause,
  IconMute,
  IconUnmute,
  IconFullscreen,
  IconFullscreenExit,
} from '@/features/ui-kit/icons';

interface Props {
  videoURL?: string;
  isMutedProp?: boolean;
  isPlayingProp?: boolean;
  isCardViewOpenedProp?: boolean;
  fullscreenEnable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  videoURL: '',
  isMutedProp: true,
  isPlayingProp: true,
  isCardViewOpenedProp: false,
  fullscreenEnable: true,
});

const emit = defineEmits<{
  close: [];
  setZIndex: [value: boolean];
}>();

const isOverVideo = ref(false);
const isMuted = ref(true);
const isPlaying = ref(true);
const isOnFullscreen = ref(false);
const isCardViewOpened = ref(false);
const bufferedWidth = ref(0);
const watchedTime = ref(0);
const video = ref<HTMLVideoElement | null>(null);
const progressEl = ref<HTMLDivElement | null>(null);

const isMobile = () => {
  return !!/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

const playPauseVideo = () => {
  if (!video.value) return;

  if (isPlaying.value) {
    video.value.pause();
  } else {
    video.value.play();
  }
  isPlaying.value = !isPlaying.value;
};

// TODO: Возможна проблема с openModal - может неправильно работать переключение режимов
// Если тесты покажут проблемы, нужно будет вернуться к этому вопросу
const openModal = () => {
  isCardViewOpened.value = !isCardViewOpened.value;
  emit('setZIndex', isCardViewOpened.value);
};

const openFullScreen = () => {
  isMobile() ? openMobileFullScreen() : openDesktopFullScreen();
};

const openDesktopFullScreen = () => {
  openModal();
  isOnFullscreen.value = !isOnFullscreen.value;
};

const openMobileFullScreen = async () => {
  if (!video.value) return;

  isOnFullscreen.value = true;

  if (!document.fullscreenElement) {
    try {
      if (video.value.requestFullscreen) {
        await video.value.requestFullscreen();
      } else if ((video.value as any).mozRequestFullScreen) {
        await (video.value as any).mozRequestFullScreen();
      } else if ((video.value as any).webkitRequestFullscreen) {
        await (video.value as any).webkitRequestFullscreen();
      } else if ((video.value as any).webkitSupportsFullscreen) {
        await (video.value as any).webkitEnterFullScreen();
      } else if ((video.value as any).msRequestFullscreen) {
        await (video.value as any).msRequestFullscreen();
      }
    } catch (error) {
      console.warn('Fullscreen error:', error);
    }
  } else {
    try {
      await document.exitFullscreen();
      isOnFullscreen.value = false;
    } catch (error) {
      console.warn('Exit fullscreen error:', error);
    }
  }
};

const mute = () => {
  if (!video.value) return;

  isMuted.value = !isMuted.value;

  if (isSafari()) {
    // hack for safari
    video.value.play();
  }
};

const progressFunc = () => {
  if (!video.value || !progressEl.value) return;

  watchedTime.value = video.value.currentTime;
  const completedInPercents = Math.round((watchedTime.value * 100) / video.value.duration);

  bufferedWidth.value =
    Math.round(progressEl.value.getBoundingClientRect().width) * (completedInPercents / 100);
};

const changeTime = (e: MouseEvent) => {
  if (!video.value || !progressEl.value) return;

  let progressData = progressEl.value.getBoundingClientRect();
  let completed = e.clientX - progressData.left;
  let completedInPercents = (completed * 100) / progressData.width;

  video.value.currentTime = (completedInPercents * video.value.duration) / 100;
};

const close = () => {
  if (isCardViewOpened.value) {
    emit('close');
  } else {
    openModal();
  }
};

const secondsToTime = computed(() => {
  let dm = watchedTime.value % (60 * 60);
  let m = Math.floor(dm / 60);
  let ds = dm % 60;
  let s = Math.ceil(ds);

  if (s === 60) {
    s = 0;
    m = m + 1;
  }

  if (m === 60) m = 0;

  const stringifiedSeconds: string = s < 10 ? `0${s}` : `${s}`;
  const stringifiedMinutes: string = m < 10 ? `0${m}` : `${m}`;

  return `${stringifiedMinutes} : ${stringifiedSeconds}`;
});

onMounted(() => {
  if (video.value) {
    video.value.addEventListener('timeupdate', progressFunc, false);
  }
});

onUnmounted(() => {
  if (video.value) {
    video.value.removeEventListener('timeupdate', progressFunc);
  }
});

watch(isCardViewOpened, (value) => {
  if (!value) {
    document.querySelector('body')?.classList.add('overflow-hidden');
  } else {
    document.querySelector('body')?.classList.remove('overflow-hidden');
  }
});

// Initialize with props
watch(
  () => [props.isMutedProp, props.isPlayingProp, props.isCardViewOpenedProp],
  () => {
    isMuted.value = props.isMutedProp;
    isPlaying.value = props.isPlayingProp;
    isCardViewOpened.value = props.isCardViewOpenedProp;
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="interactive-content"
    id="interactive-content"
    :class="{ 'interactive-content--card-view': isCardViewOpened }"
    @click.self="close"
    @mouseleave="isOverVideo = false"
    @mouseover="isOverVideo = true"
  >
    <div class="video-controls">
      <div class="close hover" @click="close">
        <IconClose :size="23" color="inverse-primary" />
      </div>
    </div>

    <div class="video-wrapper" :class="{ 'video-wrapper--card-view': isCardViewOpened }">
      <video
        class="video-content"
        :class="{
          'video-content--card-view': isCardViewOpened,
          'video-content--fullscreen': isOnFullscreen,
        }"
        autoplay
        loop
        playsinline
        ref="video"
        :muted="isMuted"
        @click="playPauseVideo"
      >
        <source :src="videoURL" type="video/mp4" />
        <source :src="videoURL" type="video/mov" />
        Ваш браузер не поддерживает видео.
      </video>

      <div class="video-shadow" @click.self="playPauseVideo">
        <div class="progress-wrapper" @click="changeTime">
          <div class="progress-bar" ref="progressEl">
            <div class="buffered" :style="{ width: bufferedWidth + 'px' }"></div>
            <span class="current-time">{{ secondsToTime }}</span>
          </div>
        </div>

        <div class="mute hover" v-if="videoURL" @click="mute">
          <IconMute v-if="isMuted" :size="16" color="inverse-primary" />
          <IconUnmute v-else-if="!isMuted" :size="16" color="inverse-primary" />
        </div>

        <div class="fullscreen" v-if="videoURL && fullscreenEnable" @click="openFullScreen">
          <IconFullscreen v-if="!isOnFullscreen" :size="16" color="inverse-primary" />
          <IconFullscreenExit v-else-if="isOnFullscreen" :size="16" color="inverse-primary" />
        </div>

        <div class="play-pause--small" @click="playPauseVideo">
          <IconPause v-if="isPlaying" :size="16" color="inverse-primary" />
          <IconPlay v-else-if="!isPlaying" :size="16" color="inverse-primary" />
        </div>
      </div>

      <div class="play-pause" v-if="!isPlaying" @click="playPauseVideo">
        <IconPlay :size="16" color="inverse-primary" />
      </div>
    </div>
  </div>
</template>

<style lang="stylus" scoped>
#interactive-content
  position fixed
  top 0
  bottom 0
  right 0
  left 0
  background rgba(0, 0, 0, .8)
  z-index 6

  .video-controls
    .close
      position absolute
      right 16px
      top 16px
      border-radius 24px
      width 48px
      height 48px
      z-index 2
      background rgba(79, 81, 85, .9)
      backdrop-filter blur(40px)
      display flex
      align-items center
      justify-content center

      &:hover
        background rgba(118, 121, 127, .9)
        backdrop-filter blur(40px)

      &.close--card-view
        z-index 1

    .hover:hover
      background rgba(136, 136, 136, .5)
      cursor pointer

  .video-wrapper
    position absolute
    left 50%
    top 50%
    transform translate(-50%, -50%)

    &.video-wrapper--card-view
      width 100%
      height 100%

    @media (max-width: 640px)
      width 100vw
      height 100vh

    &:hover
      .video-shadow
        background linear-gradient(180deg, rgba(0, 0, 0, 0) 0, rgba(0, 0, 0, .1) 25%, rgba(0, 0, 0, .4) 100%)

        .buffered,
        .progress-bar span,
        .mute,
        .fullscreen,
        .play-pause--small
          position absolute
          opacity 1

        .mute,
        .fullscreen,
        .play-pause--small
          position absolute
          bottom 28px
          border-radius 24px
          width 32px
          height 32px
          display flex
          align-items center
          justify-content center

          &:hover
            cursor pointer
            background rgba(79, 81, 85, .9)
            backdrop-filter blur(40px)

        .mute
          right 5px

        .fullscreen
          right 45px

        .play-pause--small
          left 5px

        .progress-wrapper
          padding-bottom 8px
          padding-top 8px
          position absolute
          bottom 8px
          left 8px
          right 8px
          height 4px
          width auto
          border-radius 2px

          .current-time
            position absolute
            bottom 20px
            left 50%
            transform translateX(-50%)
            padding 1.5px 8px
            height 16px
            border-radius 9px
            font-family Inter, sans-serif
            font-style normal
            font-weight normal
            font-size 11px
            line-height 13px
            display flex
            align-items center
            text-align center
            color #FFFFFF
            flex none
            order 0
            align-self center
            background rgba(79, 81, 85, .9)
            backdrop-filter blur(40px)

          &:hover
            cursor pointer

            .buffered::before
              display block
              content ''
              position absolute
              top -4px
              right -4px
              height 12px
              width 12px
              background #FFFFFF
              border-radius 25px

          .progress-bar
            position absolute
            left -50%
            height 4px
            width 100%
            background rgba(255, 255, 255, .5)
            border-radius 2px
            transform translate(50%, 50%)
            top 4px

            .buffered
              position absolute
              top 0
              left 0
              height 4px
              background #FFFFFF
              border-radius 2px
              transition 150ms width

              &::before:hover
                cursor pointer

    .video-content
      // remove black border trick
      clip-path inset(0)
      width auto
      height 100vh

      &.video-content--card-view
        width 100%
        height 100%
        object-fit cover

      @media (max-width: 640px)
        width 100vw
        height 100vh
        object-fit cover

    .video-content--fullscreen
      left 50%
      top 50%
      position absolute
      transform translate(-50%, -50%)

    .video-shadow
      width 100%
      height 96px
      bottom 0
      left 0
      position absolute

      .buffered,
      .progress-bar span
        position absolute
        opacity 0

      .mute,
      .fullscreen,
      .play-pause--small
        position absolute
        opacity 1
        bottom 28px
        border-radius 24px
        width 32px
        height 32px
        display flex
        align-items center
        justify-content center

        &:hover
          cursor pointer
          background rgba(79, 81, 85, .9)
          backdrop-filter blur(40px)

      .mute
        right 5px

      .fullscreen
        right 45px

      .play-pause--small
        left 5px

  .play-pause
    width 48px
    height 48px
    position absolute
    left 50%
    top 50%
    transform translate(-50%, -50%)
    bottom 28px
    border-radius 24px
    background rgba(79, 81, 85, .9)
    backdrop-filter blur(40px)
    display flex
    align-items center
    justify-content center

    &:hover
      background rgba(118, 121, 127, .9)
      cursor pointer
      backdrop-filter blur(40px)

#interactive-content.interactive-content--card-view
  width 100%
  height 100%
  position absolute
  top 0
  left 0
  background unset
  z-index 1

  .video-controls
    .close
      z-index 1
      width 32px
      height 32px

  .video-wrapper
    width 100%
    height 100%

  .video-content
    width 100%
    height 100%
    object-fit cover

  .video-shadow
    width 100%
    height 96px
    bottom 0
    left 0
    position absolute

    .progress-wrapper
      padding-bottom 8px
      padding-top 8px
      position absolute
      bottom 8px
      left 8px
      right 8px
      height 4px
      width auto
      border-radius 2px

      .current-time
        position absolute
        bottom 20px
        left 50%
        transform translateX(-50%)
        padding 1.5px 8px
        height 16px
        border-radius 9px
        font-family Inter, sans-serif
        font-style normal
        font-weight normal
        font-size 11px
        line-height 13px
        display flex
        align-items center
        text-align center
        color #FFFFFF
        flex none
        order 0
        align-self center
        background rgba(79, 81, 85, .9)
        backdrop-filter blur(40px)

      &:hover
        cursor pointer

        .buffered::before
          display block
          content ''
          position absolute
          top -4px
          right -4px
          height 12px
          width 12px
          background #FFFFFF
          border-radius 25px

      .progress-bar
        position absolute
        left -50%
        height 4px
        width 100%
        background rgba(255, 255, 255, .5)
        border-radius 2px
        transform translate(50%, 50%)
        top 4px

        .buffered
          position absolute
          top 0
          left 0
          height 4px
          background #FFFFFF
          border-radius 2px
          transition 150ms width

          &::before:hover
            cursor pointer

    .mute,
    .fullscreen,
    .play-pause--small
      position absolute
      bottom 28px
      border-radius 24px
      width 32px
      height 32px
      display flex
      align-items center
      justify-content center

      &:hover
        cursor pointer
        background rgba(118, 121, 127, .9)
        backdrop-filter blur(40px)

    .mute
      right 5px

    .fullscreen
      right 45px

    .play-pause--small
      left 5px

    &:hover
      background linear-gradient(180deg, rgba(0, 0, 0, 0) 0, rgba(0, 0, 0, .1) 25%, rgba(0, 0, 0, .4) 100%)

  .play-pause
    width 32px
    height 32px
    position absolute
    left 50%
    top 50%
    transform translate(-50%, -50%)
    bottom 28px
    background rgba(79, 81, 85, .9)
    backdrop-filter blur(40px)
    border-radius 24px
    display flex
    align-items center
    justify-content center

    &:hover
      background rgba(118, 121, 127, .9)
      cursor pointer
      backdrop-filter blur(40px)
</style>
